import type { Span } from '@jsonfmt/core';
import { createContext, useContext } from 'react';

/**
 * THE ONE CHANNEL THAT POINTS *INTO* THE EDITOR — ADR-0014 (`UiPort`), ADR-0006.
 *
 * Everything else in this architecture flows one way: the store owns the text, commands return
 * data, panels receive props. Two things genuinely cannot be expressed that way, and `UiPort`
 * in `features/types.ts` names exactly those two: "put the caret in the editor" and "show me
 * this span". They are imperative because a caret is not derivable from a document — the same
 * document has infinitely many valid caret positions, so there is no state to diff.
 *
 * `UiPort` lives in `src/app/`, and the editor is mounted three layers below it inside a lazily
 * imported panel. The handle therefore has to travel UPWARDS, and this module is that wire.
 *
 * ── WHY IT IS ITS OWN MODULE, AND WHY IT IMPORTS NO `@codemirror/*` ───────────────────────
 *
 * The provider is mounted by `src/app/`, which is eager; the panel that fills it is behind
 * `ViewDescriptor.load()`, which is lazy. A context declared inside `CodeMirrorHost.tsx` would
 * force `src/app/` to import that module in order to mount the provider, and the editor engine
 * would land in the initial chunk — the same reasoning that put `EditorDocumentContext` in
 * `documentSource.ts` rather than in the host. `Span` is a pure engine type and costs nothing.
 *
 * ── WHY A SINK AND NOT A `useImperativeHandle` REF ───────────────────────────────────────
 *
 * A ref would work only if `src/app/` rendered the host itself. It does not: `ViewHost` spreads
 * `select(doc)`'s output into a component it has never heard of, so there is no ref to forward.
 * Registration inverts that: whoever mounts an editor announces itself, and whoever unmounts
 * one withdraws. `register` returns its own un-register, so a double mount (React 19 StrictMode
 * mounts every effect twice in development) cannot leave a dead handle behind.
 */

export interface EditorRevealOptions {
  /** Default `true`. `false` scrolls and highlights without stealing the keyboard. */
  readonly focus?: boolean;
}

/**
 * What the mounted editor can be asked to do. Deliberately narrow; every addition here is an
 * addition to the imperative surface of the whole application.
 *
 * `undo` is the fourth, and it is here for the same reason the first three are: the undo
 * HISTORY is state CodeMirror owns and `DocumentStore` deliberately does not (ADR-0006 — one
 * writer, and the history belongs to the view that recorded the transactions). There is no
 * document to diff into "one step earlier", so the only way to express it is to ask.
 *
 * What it buys is docs/DESIGN.md §6.5's `Undo` button on the repair banner: "a peer of the
 * message, not an item hidden in a toast". Before it, the ONLY way to reverse a repair the
 * tool had performed on the user's bytes without asking was to know ⌘Z — and that shortcut
 * reached CodeMirror only while the editor had focus, so a user who had just clicked the
 * banner's × had no way back at all. ADR-0026 records the choice.
 */
export interface EditorHandle {
  /** Moves the keyboard into the document. No-op if the view is gone. */
  focus(): void;
  /**
   * Reverses the last history step, exactly as ⌘Z would. `false` when there was nothing to
   * undo (or no view), so a caller can stay quiet rather than claim it did something.
   *
   * One repair is one step: ADR-0006 puts the whole repair-and-format in a single
   * transaction precisely so that this is true.
   */
  undo(): boolean;
  /**
   * Selects `span`, scrolls it to the middle of the viewport and tints its line `--lime-sub`
   * until the next edit (docs/DESIGN.md §5). Offsets past the end of the document are clamped
   * rather than thrown on: a report may legitimately be one version behind (ADR-0009).
   */
  revealSpan(span: Span, options?: EditorRevealOptions): void;
  /** `true` while the editor has DOM focus. `UiPort` uses it to decide whether to steal it. */
  hasFocus(): boolean;
  /**
   * How many pixels at the BOTTOM of the editor are covered by something else — the phone
   * diagnostics sheet (ADR-0101 §8.5). Every later `revealSpan` scrolls the span above that
   * band instead of underneath it.
   *
   * The fifth method, and the only one that is not a verb the user could name. It exists
   * because the sheet overlays the code rather than displacing it, which is itself a decision:
   * displacing would reflow a 5000-line document every time the drawer opened, and on a 375px
   * phone there is no room to displace INTO. So the code keeps its box and the editor is told
   * that part of that box is not visible — which is exactly what CodeMirror's `scrollMargins`
   * facet is for.
   *
   * It cannot be derived here. The sheet's height depends on how many diagnostics there are,
   * on the soft keyboard, and on whether the layout has switched to the desktop column at all;
   * only the composition root, which owns the drawer, can measure it. `0` resets it, and that
   * is what the shell sends on close and at ≥ 960px.
   */
  setScrollInset(px: number): void;
}

export interface EditorHandleSink {
  /** Announces a mounted editor. The returned function withdraws it. */
  register(handle: EditorHandle): () => void;
}

export const EditorHandleContext = createContext<EditorHandleSink | null>(null);

/**
 * The sink, or `null` when nobody is listening.
 *
 * `null` rather than a throw, unlike `useEditorDocumentSource`: an editor with no document is a
 * wiring bug, but an editor nobody wants a handle on is a perfectly good editor — that is how
 * `CodeMirrorHost.test.tsx` mounts one, and how a future read-only diff pane would.
 */
export function useEditorHandleSink(): EditorHandleSink | null {
  return useContext(EditorHandleContext);
}

/**
 * A sink that remembers the last handle registered, for `src/app/` to build `UiPort` from.
 *
 * Plain, DOM-free and React-free, so the composition root can construct one in a `useRef`
 * initialiser and `UiPort.focusEditor()` becomes `sink.current?.focus()`. Handles are held in
 * registration order and the LAST one wins: with one editor on screen that is the only one,
 * and with two (a Diff view) the most recently mounted pane is the one a global shortcut means.
 */
export function createEditorHandleSink(): EditorHandleSink & {
  readonly current: EditorHandle | null;
} {
  const handles: EditorHandle[] = [];

  return {
    register(handle) {
      handles.push(handle);
      return () => {
        const at = handles.lastIndexOf(handle);
        if (at >= 0) handles.splice(at, 1);
      };
    },
    get current() {
      return handles.length === 0 ? null : (handles[handles.length - 1] ?? null);
    },
  };
}
